import { themeVars } from '@libs/shared-ui-styles';
import { useSVGSprite } from '../DynamicSVGSpritesheetProvider';

const getBlueGreenGradientID = (id: string) => `${id}-bg-grad`;
const getRedYellowGradientID = (id: string) => `${id}-ry-grad`;

// To optimize performance, we'll render the icon once somewhere
// on the page with this source component, and then we can
// re-use it with the TranslationIcon component
const TranslationIconSprite = ({ id }: { id: string }) => (
  <>
    <symbol id={id} viewBox="0 0 18 19">
      <g stroke="none">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M2.97861 15.25L15.875 4.33767V15.25H2.97861ZM1.30581 16.0105C1.20897 15.896 1.17246 15.7507 1.19303 15.6133C1.14911 15.5007 1.125 15.3782 1.125 15.25V3.75C1.125 3.19772 1.57272 2.75 2.125 2.75H15.875C16.0031 2.75 16.1256 2.77409 16.2382 2.81799C16.4038 2.79325 16.5781 2.85228 16.6942 2.98953C16.791 3.10397 16.8275 3.24926 16.807 3.38672C16.8509 3.49932 16.875 3.62185 16.875 3.75V15.25C16.875 15.8023 16.4273 16.25 15.875 16.25H2.125C1.99689 16.25 1.87441 16.2259 1.76184 16.182C1.59619 16.2068 1.42194 16.1477 1.30581 16.0105ZM7.30273 10.2812L2.125 14.6623L2.125 3.75H15.0214L7.80711 9.85439C7.82636 9.72409 7.79441 9.58645 7.70717 9.47428L5.84375 7.07845V6.625H7.59375C7.86989 6.625 8.09375 6.40114 8.09375 6.125C8.09375 5.84886 7.86989 5.625 7.59375 5.625H5.84375L5.84374 4.71874C5.84374 4.4426 5.61988 4.21875 5.34374 4.21875C5.06759 4.21875 4.84374 4.44261 4.84374 4.71876L4.84375 5.625H3.09375C2.81761 5.625 2.59375 5.84886 2.59375 6.125C2.59375 6.40114 2.81761 6.625 3.09375 6.625H4.84375V7.07845L2.98032 9.47428C2.81078 9.69225 2.85005 10.0064 3.06802 10.1759C3.28599 10.3455 3.60013 10.3062 3.76967 10.0882L5.34375 8.06441L6.91781 10.0882C7.01415 10.2121 7.15719 10.2782 7.30273 10.2812ZM12.9375 8.15625C12.3538 8.15625 11.8862 8.40843 11.5383 8.81425C11.2053 9.20279 10.9869 9.72386 10.8372 10.2852C10.5377 11.4084 10.4688 12.872 10.4688 14.2812C10.4688 14.5574 10.6926 14.7812 10.9688 14.7812C11.2449 14.7812 11.4688 14.5574 11.4688 14.2812C11.4688 13.7801 11.4779 13.2858 11.5016 12.8125H14.3734C14.3971 13.2858 14.4062 13.7801 14.4062 14.2812C14.4062 14.5574 14.6301 14.7812 14.9062 14.7812C15.1824 14.7812 15.4062 14.5574 15.4062 14.2812C15.4062 12.872 15.3373 11.4084 15.0378 10.2852C14.8881 9.72386 14.6697 9.20279 14.3367 8.81425C13.9888 8.40843 13.5212 8.15625 12.9375 8.15625ZM14.2956 11.8125C14.2444 11.3479 14.1719 10.919 14.0716 10.5429C13.94 10.0496 13.7717 9.69174 13.5774 9.46504C13.3979 9.25563 13.1976 9.15625 12.9375 9.15625C12.6774 9.15625 12.4771 9.25563 12.2976 9.46504C12.1033 9.69174 11.935 10.0496 11.8034 10.5429C11.7031 10.919 11.6306 11.3479 11.5794 11.8125H14.2956Z"
        />
      </g>
    </symbol>
    {/* Blue/green gradient */}
    <radialGradient
      id={getBlueGreenGradientID(id)}
      cx="0"
      cy="0"
      r="1"
      gradientUnits="userSpaceOnUse"
      gradientTransform="translate(1.24069 3.37373) rotate(42.3794) scale(18.3614 30.0121)"
    >
      <stop stopColor={themeVars.color.ai.color1} />
      <stop offset="1" stopColor={themeVars.color.brand.default} />
    </radialGradient>
    {/* Pink/yellow gradient */}
    <radialGradient
      id={getRedYellowGradientID(id)}
      cx="0"
      cy="0"
      r="1"
      gradientUnits="userSpaceOnUse"
      gradientTransform="translate(1.24069 3.37373) rotate(42.3794) scale(18.3614 30.0121)"
    >
      <stop stopColor={themeVars.color.ai.color2} />
      <stop offset="1" stopColor={themeVars.color.ai.color1} />
    </radialGradient>
  </>
);

/**
 * Translation icon with a gradient fill. This icon is fairly complex and can appear a large number of times on the same page,
 * so we are using the SVGIconSourceProvider to optimize performance by rendering the source icon once and re-use it for every
 * TranslationIcon instance.
 */
export const TranslationIcon = ({
  gradientType = 'blueGreen',
  ...props
}: React.ComponentPropsWithoutRef<'svg'> & {
  gradientType?: 'blueGreen' | 'redYellow';
}) => {
  // Register the TranslationIconSprite component so we can reference it via <use>
  const iconID = useSVGSprite(TranslationIconSprite);

  return (
    <svg width="20" height="21" viewBox="0 0 18 19" xmlns="http://www.w3.org/2000/svg" {...props}>
      <use
        href={`#${iconID}`}
        fill={`url(#${
          gradientType === 'blueGreen'
            ? getBlueGreenGradientID(iconID)
            : getRedYellowGradientID(iconID)
        })`}
      />
    </svg>
  );
};

interface TranslationIconSolidProps extends React.ComponentPropsWithoutRef<'svg'> {
  color?: string;
}

export const TranslationIconSolid = ({ color = 'currentColor' }: TranslationIconSolidProps) => (
  <svg width="20" height="21" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.97861 15.25L15.875 4.33767V15.25H2.97861ZM1.30581 16.0105C1.20897 15.896 1.17246 15.7507 1.19303 15.6133C1.14911 15.5007 1.125 15.3782 1.125 15.25V3.75C1.125 3.19772 1.57272 2.75 2.125 2.75H15.875C16.0031 2.75 16.1256 2.77409 16.2382 2.81799C16.4038 2.79325 16.5781 2.85228 16.6942 2.98953C16.791 3.10397 16.8275 3.24926 16.807 3.38672C16.8509 3.49932 16.875 3.62185 16.875 3.75V15.25C16.875 15.8023 16.4273 16.25 15.875 16.25H2.125C1.99689 16.25 1.87441 16.2259 1.76184 16.182C1.59619 16.2068 1.42194 16.1477 1.30581 16.0105ZM7.30273 10.2812L2.125 14.6623L2.125 3.75H15.0214L7.80711 9.85439C7.82636 9.72409 7.79441 9.58645 7.70717 9.47428L5.84375 7.07845V6.625H7.59375C7.86989 6.625 8.09375 6.40114 8.09375 6.125C8.09375 5.84886 7.86989 5.625 7.59375 5.625H5.84375L5.84374 4.71874C5.84374 4.4426 5.61988 4.21875 5.34374 4.21875C5.06759 4.21875 4.84374 4.44261 4.84374 4.71876L4.84375 5.625H3.09375C2.81761 5.625 2.59375 5.84886 2.59375 6.125C2.59375 6.40114 2.81761 6.625 3.09375 6.625H4.84375V7.07845L2.98032 9.47428C2.81078 9.69225 2.85005 10.0064 3.06802 10.1759C3.28599 10.3455 3.60013 10.3062 3.76967 10.0882L5.34375 8.06441L6.91781 10.0882C7.01415 10.2121 7.15719 10.2782 7.30273 10.2812ZM12.9375 8.15625C12.3538 8.15625 11.8862 8.40843 11.5383 8.81425C11.2053 9.20279 10.9869 9.72386 10.8372 10.2852C10.5377 11.4084 10.4688 12.872 10.4688 14.2812C10.4688 14.5574 10.6926 14.7812 10.9688 14.7812C11.2449 14.7812 11.4688 14.5574 11.4688 14.2812C11.4688 13.7801 11.4779 13.2858 11.5016 12.8125H14.3734C14.3971 13.2858 14.4062 13.7801 14.4062 14.2812C14.4062 14.5574 14.6301 14.7812 14.9062 14.7812C15.1824 14.7812 15.4062 14.5574 15.4062 14.2812C15.4062 12.872 15.3373 11.4084 15.0378 10.2852C14.8881 9.72386 14.6697 9.20279 14.3367 8.81425C13.9888 8.40843 13.5212 8.15625 12.9375 8.15625ZM14.2956 11.8125C14.2444 11.3479 14.1719 10.919 14.0716 10.5429C13.94 10.0496 13.7717 9.69174 13.5774 9.46504C13.3979 9.25563 13.1976 9.15625 12.9375 9.15625C12.6774 9.15625 12.4771 9.25563 12.2976 9.46504C12.1033 9.69174 11.935 10.0496 11.8034 10.5429C11.7031 10.919 11.6306 11.3479 11.5794 11.8125H14.2956Z"
      fill={color}
      fillOpacity="0.96"
    />
  </svg>
);
